/**
 * Fake File System — simulated Windows Explorer file tree
 * Students never access the real filesystem; this provides a safe,
 * pre-populated directory tree for Open/Save/Import dialogs.
 */

export interface FsNode {
  name: string;
  type: 'folder' | 'file';
  path: string;
  /** URL served by our express static middleware (images, SVGs) */
  assetUrl?: string;
  /** MIME-like hint: 'image', 'svg', 'project', 'media', 'ae', 'shot' */
  fileType?: string;
  extension?: string;
  /** Maps to a registered template ID (for SVG title files) */
  templateId?: string;
  /** Thumbnail image URL for non-image files (e.g. video) */
  thumbUrl?: string;
  children?: FsNode[];
}

// ── Helper: build a file node ─────────────────────────────────
function file(
  name: string,
  path: string,
  opts: { assetUrl?: string; fileType?: string; extension?: string; templateId?: string; thumbUrl?: string } = {},
): FsNode {
  return { name, type: 'file', path, ...opts };
}

function folder(name: string, path: string, children: FsNode[] = []): FsNode {
  return { name, type: 'folder', path, children };
}

// ── The fake tree ─────────────────────────────────────────────
const ROOT = 'This PC/Projects/ScoreView SIMS';

const svAds = folder('SV Ads', `${ROOT}/Lesson Media/SV Ads`, [
  file('Ad Logo 1.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 1.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 1.png', fileType: 'image', extension: '.png' }),
  file('Ad Logo 2.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 2.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 2.png', fileType: 'image', extension: '.png' }),
  file('Ad Logo 3.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 3.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 3.png', fileType: 'image', extension: '.png' }),
  file('Ad Logo 4.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 4.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 4.png', fileType: 'image', extension: '.png' }),
  file('Ad Logo 5.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 5.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 5.png', fileType: 'image', extension: '.png' }),
  file('Ad Logo 6.png', `${ROOT}/Lesson Media/SV Ads/Ad Logo 6.png`, { assetUrl: '/assets/Lesson Media/Ad Logo 6.png', fileType: 'image', extension: '.png' }),
]);

const lessonMedia = folder('Lesson Media', `${ROOT}/Lesson Media`, [
  svAds,
  file('Atlas_Pet_Vets.png', `${ROOT}/Lesson Media/Atlas_Pet_Vets.png`, { assetUrl: '/assets/Lesson Media/Atlas_Pet_Vets.png', fileType: 'image', extension: '.png' }),
  file('Andrews_Commercial_2021.webm', `${ROOT}/Lesson Media/Andrews_Commercial_2021.webm`, { assetUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Andrews_Commercial_2021.webm', fileType: 'media', extension: '.webm', thumbUrl: '/assets/Lesson Media/Andrews_Commercial_2021.jpg' }),
  file('Sponsor Background Blank.png', `${ROOT}/Lesson Media/Sponsor Background Blank.png`, { assetUrl: '/assets/Lesson Media/Sponsor Background Blank.png', fileType: 'image', extension: '.png' }),
  file('Sponsor Background Full.png', `${ROOT}/Lesson Media/Sponsor Background Full.png`, { assetUrl: '/assets/Lesson Media/Sponsor Background Full.png', fileType: 'image', extension: '.png' }),
  file('Team Logo 1.png', `${ROOT}/Lesson Media/Team Logo 1.png`, { assetUrl: '/assets/Lesson Media/Team Logo 1.png', fileType: 'image', extension: '.png' }),
  file('Team Logo 2.png', `${ROOT}/Lesson Media/Team Logo 2.png`, { assetUrl: '/assets/Lesson Media/Team Logo 2.png', fileType: 'image', extension: '.png' }),
]);

const titles = folder('Titles', `${ROOT}/Titles`, [
  file('Football Bottom Scoreboard.svg', `${ROOT}/Titles/Football Bottom Scoreboard.svg`, { assetUrl: '/assets/titles/Football Bottom Scoreboard.svg', fileType: 'svg', extension: '.svg', templateId: 'fb-bottom-scoreboard' }),
  file('Football Halftime Stats.svg', `${ROOT}/Titles/Football Halftime Stats.svg`, { assetUrl: '/assets/titles/Football Halftime Stats.svg', fileType: 'svg', extension: '.svg', templateId: 'fb-halftime-stats' }),
]);

const projects = folder('Projects', `${ROOT}/Projects`, []);

const scoreViewSIMS = folder('ScoreView SIMS', ROOT, [
  lessonMedia,
  titles,
  projects,
]);

// Sidebar destinations — most resolve to empty folders
const sidebar: FsNode[] = [
  folder('Quick access', 'Quick access', []),
  folder('Desktop', 'Desktop', []),
  folder('Downloads', 'Downloads', []),
  folder('Documents', 'Documents', []),
  folder('Pictures', 'Pictures', []),
  folder('Videos', 'Videos', []),
  folder('OneDrive - Personal', 'OneDrive - Personal', []),
  folder('This PC', 'This PC', [
    folder('This PC/Projects', 'This PC/Projects', [
      scoreViewSIMS,
    ]),
  ]),
  folder('Network', 'Network', []),
];

// ── Build flat lookup ─────────────────────────────────────────
const nodeIndex = new Map<string, FsNode>();

function indexTree(node: FsNode): void {
  nodeIndex.set(node.path, node);
  if (node.children) {
    for (const child of node.children) {
      indexTree(child);
    }
  }
}

// Index sidebar roots and the ScoreView tree
for (const s of sidebar) indexTree(s);

// ── Public helpers ────────────────────────────────────────────

export function getFsNode(path: string): FsNode | undefined {
  return nodeIndex.get(path);
}

export function getChildren(path: string): FsNode[] {
  const node = nodeIndex.get(path);
  return node?.children ?? [];
}

export function matchesFilter(node: FsNode, filter: string): boolean {
  if (node.type === 'folder') return true;
  if (!filter || filter === '*.*' || filter === 'All Files (*.*)') return true;

  // Extract extensions from filter string like "SVG Files (*.svg)|*.svg"
  // or "Image Files (*.png;*.jpg)|*.png;*.jpg"
  const extMatch = filter.match(/\*\.(\w+)/g);
  if (!extMatch) return true;
  const allowedExts = extMatch.map((m) => m.replace('*', '').toLowerCase());
  const ext = (node.extension || '').toLowerCase();
  return allowedExts.some((a) => a === ext);
}

export function getSidebarItems(): FsNode[] {
  return sidebar;
}

// Re-export common paths for convenience
export const PATHS = {
  ROOT: ROOT,
  LESSON_MEDIA: `${ROOT}/Lesson Media`,
  SV_ADS: `${ROOT}/Lesson Media/SV Ads`,
  TITLES: `${ROOT}/Titles`,
  PROJECTS: `${ROOT}/Projects`,
} as const;
